/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, March 8th 2022, 8:43:54 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { OSAPIFormWithHook } from "ui-widgets/forms";
import { FormConfiguration, FormConfigurationValidationSchema } from "lib/schemas";
import { APISERVICEPATHS } from "lib/api";
import { StyledOSDialog, StyledOSDialogProps } from "ui-widgets";
import { FileText } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { useDeepEffect } from "lib/deep_hooks";
import { useFormBuilderContext } from "form-builder/providers";
import { useFormBuilderStore, currentFormSelector } from "form-builder/stores";

/** Reusable form component for patching a form model on the backend. Use this when you want to quickly patch
 * the form model with some data from a component that manages that data e.g. registration form settings,
 * Form Identifier Configuration (IR Sequence), General info etc.
 *
 * Children for this component must be set of valid OSFormFields
 */

export interface FormAPIPatchFormProps
  extends Pick<StyledOSDialogProps, "popUpIcon" | "popUpTitle" | "popUpDescription"> {
  closeFormInfoPopUpFn: () => void;
  formInfoPopUpOpenState: boolean;
  defaultNewFormConfiguration?: FormConfiguration;
  onAPISaveFormInfoCallBackFn?: (formConfiguration: FormConfiguration) => void;
  children: React.ReactNode;
}

export const FormAPIPatchForm = ({
  closeFormInfoPopUpFn,
  formInfoPopUpOpenState,
  defaultNewFormConfiguration,
  popUpTitle,
  popUpDescription,
  onAPISaveFormInfoCallBackFn,
  children,
  ...props
}: FormAPIPatchFormProps) => {
  const { portalIds = [] } = useFormBuilderContext();
  const currentFormConfiguration = useFormBuilderStore(currentFormSelector);
  const setCurrentForm = useFormBuilderStore((state) => state.setCurrentForm);

  const setProviderFormConfiguration = React.useCallback(
    (formConfiguration) => {
      setCurrentForm(formConfiguration);
      onAPISaveFormInfoCallBackFn?.(formConfiguration);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useDeepEffect(() => {
    // Ensure the form always has sites set.
    if (portalIds?.length && currentFormConfiguration?.sites.length === 0)
      setProviderFormConfiguration({ ...currentFormConfiguration, sites: [...portalIds] });
  }, [portalIds, currentFormConfiguration]);

  const formPopUpTitle = popUpTitle ?? "Form Info";
  const formPopUpIconComponent = props.popUpIcon ?? <FileText color={COLORSCHEME.primaryVariant} />;

  return (
    <StyledOSDialog
      popUpIcon={formPopUpIconComponent}
      popUpTitle={formPopUpTitle}
      open={formInfoPopUpOpenState}
      onClose={() => closeFormInfoPopUpFn()}
      popUpDescription={popUpDescription ?? "Provide information about the form below."}
    >
      <OSAPIFormWithHook
        apiServicePath={APISERVICEPATHS.forms}
        apiInitialFetchId={null}
        apiFormDefaultValues={defaultNewFormConfiguration ?? currentFormConfiguration}
        apiFormDataValidationSchema={FormConfigurationValidationSchema}
        setAPIFormResponse={setProviderFormConfiguration}
        primaryKey="id"
        titleKey="title"
        method="PATCH"
        loaderType="form-skeleton"
      >
        {children}
      </OSAPIFormWithHook>
    </StyledOSDialog>
  );
};
